/*
 * 模块: 任务状态监控器
 * 职责: 轮询异步任务状态并更新进度
 * 依赖: Ant Design, API
 * 导出: 任务监控函数
 * 限制: 文件<280行, 函数<30行
 */

import { message } from 'antd';
import type { ProgressUpdater } from './fileProcessor';

/**
 * 任务状态检查函数类型
 */
export type TaskChecker = (taskId: string) => Promise<{
  data: {
    state: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
    current?: number;
    total?: number;
  };
}>;

/**
 * 批量上传结果项
 */
interface BatchResult {
  file: string;
  status: string;
  week_number?: string;
}

/**
 * 显示任务进度
 */
const showTaskProgress = (
  current: number,
  total: number,
  msgKey: string
): void => {
  const percentage = Math.round((current / total) * 100);
  message.loading({
    content: `处理关键词数据: ${current}/${total} (${percentage}%)`,
    key: msgKey,
    duration: 0
  });
};

/**
 * 更新文件处理状态
 */
const updateFileStatuses = (
  batchResults: BatchResult[],
  current: number,
  updateProgress: ProgressUpdater
): void => {
  batchResults.forEach((result, index) => {
    if (result.status === 'success') {
      const fileCompleted = index < current;
      const status = fileCompleted 
        ? `✓ ${result.week_number}` 
        : `⏳ ${result.week_number} (处理中...)`;
      updateProgress(result.file, status);
    }
  });
};

/**
 * 处理任务状态
 */
const handleTaskState = (
  state: string,
  current: number | undefined,
  total: number | undefined,
  msgKey: string,
  batchResults: BatchResult[],
  updateProgress: ProgressUpdater
): boolean => {
  if (state === 'PROGRESS') {
    if (current && total) {
      showTaskProgress(current, total, msgKey);
      updateFileStatuses(batchResults, current, updateProgress);
    }
    return false;
  }

  if (state === 'SUCCESS') {
    message.success({ content: '所有数据处理完成！', key: msgKey, duration: 2 });
    batchResults.forEach(result => {
      if (result.status === 'success') {
        updateProgress(result.file, `✓ ${result.week_number}`);
      }
    });
    return true;
  }

  if (state === 'FAILURE') {
    message.error({ content: '处理失败，请重试', key: msgKey, duration: 3 });
    return true;
  }

  if (state === 'PENDING') {
    message.loading({ content: '等待处理队列...', key: msgKey, duration: 0 });
  }

  return false;
};

/**
 * 等待异步任务完成（真实的任务状态轮询）
 */
export const waitForTaskCompletion = async (
  taskId: string,
  batchResults: BatchResult[],
  checkTaskStatus: TaskChecker,
  updateProgress: ProgressUpdater
): Promise<void> => {
  const msgKey = 'batch_processing';
  message.loading({ 
    content: '正在处理关键词数据，请稍候...', 
    key: msgKey, 
    duration: 0 
  });

  const maxAttempts = 300; // 最多等待5分钟
  let attempts = 0;
  let taskCompleted = false;

  while (attempts < maxAttempts && !taskCompleted) {
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      const response = await checkTaskStatus(taskId);
      const { state, current, total } = response.data;
      
      attempts++;

      taskCompleted = handleTaskState(
        state,
        current,
        total,
        msgKey,
        batchResults,
        updateProgress
      );

      // 避免过于频繁的轮询
      if (attempts % 2 === 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error('检查任务状态失败:', error);
      
      // 超过1分钟后如果还有错误，就停止
      if (attempts > 60) {
        message.warning({ 
          content: '无法获取处理状态，请刷新页面查看结果', 
          key: msgKey 
        });
        break;
      }
    }
  }

  // 超时处理
  if (!taskCompleted && attempts >= maxAttempts) {
    message.warning({
      content: '处理时间较长，请稍后刷新页面查看结果',
      key: msgKey,
      duration: 5
    });
  }
};

